import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ChevronRight, Scissors, Tv, UtensilsCrossed } from 'lucide-react';
import { useI18n } from '../contexts/I18nContext';
import api from '../utils/api';

const PanasonicWorldsPage = () => {
  const navigate = useNavigate();
  const { t, lang } = useI18n();
  const [worlds, setWorlds] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchWorlds();
  }, [lang]);

  const fetchWorlds = async () => {
    try {
      const response = await api.get('/panasonic/worlds');
      setWorlds(response.data);
    } catch (error) {
      console.error('Error fetching worlds:', error);
    } finally {
      setLoading(false);
    }
  };

  // Icon mapping for product worlds
  const getWorldIcon = (slug) => {
    if (slug.includes('haarschneidemaschinen')) {
      return <Scissors className="h-12 w-12 mb-4 text-primary" />;
    }
    if (slug.includes('haushalt')) {
      return <UtensilsCrossed className="h-12 w-12 mb-4 text-primary" />;
    }
    return <Tv className="h-12 w-12 mb-4 text-primary" />;
  };

  // Background image mapping (same images as HomePage)
  const getWorldImage = (slug) => {
    if (slug.includes('haarschneidemaschinen')) {
      return 'https://customer-assets-4nw71qhi.emergentagent.net/job_775d24be-5a96-4a08-95c2-9c74b17b74f4/artifacts/5606fc4m_haarschneidemaschinen.png';
    }
    if (slug.includes('haushalt')) {
      return 'https://customer-assets-4nw71qhi.emergentagent.net/job_775d24be-5a96-4a08-95c2-9c74b17b74f4/artifacts/l0d107ti_Haushalt.PNG';
    }
    return 'https://customer-assets-4nw71qhi.emergentagent.net/job_775d24be-5a96-4a08-95c2-9c74b17b74f4/artifacts/89n1pg7a_Unterhaltungselektronik.PNG';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="text-center mb-12">
        <div className="flex items-center justify-center gap-3 mb-4">
          <span
            className="text-2xl font-extrabold tracking-tight"
            style={{ color: '#0056a8', fontFamily: 'Arial, Helvetica, sans-serif' }}
            data-testid="panasonic-logo"
          >
            Panasonic
          </span>
        </div>
        <h1 className="text-3xl md:text-4xl font-bold mb-3">{t('productWorlds')}</h1>
        <p className="text-gray-600 text-lg">
          {t('chooseArea')}
        </p>
      </div>

      {/* Product World Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
        {worlds.map((world) => (
          <div
            key={world.id}
            onClick={() => navigate(`/panasonic/${world.slug}`)}
            className="group relative bg-white border-2 border-border rounded-xl overflow-hidden cursor-pointer hover:border-primary hover:shadow-xl transition-all duration-300"
            data-testid={`world-card-${world.slug}`}
          >
            {/* Background Image */}
            <div 
              className="h-48 bg-cover bg-center relative"
              style={{ backgroundImage: `url(${world.image_url || getWorldImage(world.slug)})` }}
            >
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
              <div className="absolute bottom-4 left-4 right-4">
                <span className="text-white/80 text-sm">Panasonic</span>
              </div>
            </div>

            {/* Content */}
            <div className="p-6">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 p-3 bg-primary/10 rounded-lg group-hover:bg-primary/20 transition-colors">
                  {getWorldIcon(world.slug)}
                </div>
                <div className="flex-1 min-w-0">
                  <h2 className="text-xl font-bold mb-2 group-hover:text-primary transition-colors">
                    {world.name}
                  </h2>
                  <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                    {world.subtitle}
                  </p>
                </div>
              </div>

              <Button 
                className="w-full mt-4 group-hover:bg-primary group-hover:text-white transition-colors"
                variant="outline"
              >
                {t('openArea')}
                <ChevronRight className="h-4 w-4 ms-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </div>
          </div>
        ))}
      </div>

      {/* Info Text */}
      <div className="text-center mt-12 text-gray-500 text-sm">
        <p>{t('originalWarranty')}</p>
      </div>
    </div>
  );
};

export default PanasonicWorldsPage;
